import { Col, Form, Row, Spin, Typography } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LabelWithToolTip from 'src/components/label-with-tooltip/label-with-tooltip';
import PinkBox from 'src/components/pink-box/pink-box';
import FileUploader from 'src/components/uploader/uploader';
import { DEFAULT_ALLOWED_IMAGE_FILES, DEFAULT_ALLOWED_OTHER_FILES, CHINA_NATIONALITY_VALUE } from 'src/constants';
import useStore from 'src/hooks/useStore';
import { IFunderSupportingDocument } from 'src/interfaces/accounts/account';
import { i18nKey } from 'src/locales/i18n';
import { IndividualBorrowerOnboardingStore } from 'src/store/user/individual-borrower-onboarding.store';
import styles from './supporting-documents.module.less';

const SupportingDocuments = () => {
	const [t] = useTranslation();
	const [form] = Form.useForm();

	const [saving, setSaving] = useState(false);

	const onboardingStore: IndividualBorrowerOnboardingStore = useStore('individualBorrowerOnboardingStore');

	const onFileDownload = async (type: string, id: string | undefined) => {
		setSaving(true);
		await onboardingStore.downloadFile(type as keyof IFunderSupportingDocument, id);
		setSaving(false);
	};

	const checkRequireChineseId = () => {
		if (onboardingStore.accountDetail?.nationality === CHINA_NATIONALITY_VALUE) {
			return true;
		}
		return false;
	};

	return (
		<>
			<PinkBox>
				<Spin spinning={saving}>
					<Form
						form={form}
						initialValues={{
							...onboardingStore.supportingDocuments
						}}>
						<div className={styles.container}>
							<Row>
								<Col span={24}>
									<Typography.Title level={4}>
										{t(i18nKey.onboarding.request.supportingDocument.title)}
									</Typography.Title>
								</Col>
							</Row>
							<Form.Item
								name="identities"
								rules={[
									{
										required: !checkRequireChineseId(),
										message:`${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									onDownload={onFileDownload}
									disabled
									typeId={'identities'}
									inputFiles={onboardingStore.supportingDocuments?.identities || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.identityFiles)}`}
									accept={DEFAULT_ALLOWED_IMAGE_FILES}
									requireUpload={!checkRequireChineseId()}
								/>
							</Form.Item>
							<Form.Item
								name="selfie"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'selfie'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.selfie || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.selfie)}`}
									accept={DEFAULT_ALLOWED_IMAGE_FILES}
								/>
							</Form.Item>
							<Form.Item
								name="residentialProof"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'residentialProof'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.residentialProof || []}
									title={
										<LabelWithToolTip
											label={t(i18nKey.onboarding.request.supportingDocument.residentialAddrProof)}
											tooltip={t(i18nKey.onboarding.request.supportingDocument.residentHint)}
										/>
									}
								/>
							</Form.Item>
							<Form.Item
								name="proofIncome"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'proofIncome'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.proofIncome || []}
									title={
										<LabelWithToolTip
											label={t(i18nKey.onboarding.request.supportingDocument.proofOfIncome)}
											tooltip={t(i18nKey.onboarding.request.supportingDocument.proofOfIncome)}
										/>
									}
								/>
							</Form.Item>

							<Form.Item
								name="bankStatementDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'bankStatementDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.bankStatementDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.bankStatement)}`}
								/>
							</Form.Item>

							<Form.Item
								name="reservationDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'reservationDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.reservationDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.reservationFormOrMm)}`}
								/>
							</Form.Item>
							<Form.Item
								name="contractExchangeDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'contractExchangeDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.contractExchangeDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.contractOfExchange)}`}
								/>
							</Form.Item>

							<Form.Item
								name="titleFloorDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'titleFloorDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.titleFloorDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.titleFloorPlan)}`}
								/>
							</Form.Item>

							<Form.Item
								name="ukFinanceDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'ukFinanceDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.ukFinanceDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.ukFinance)}`}
								/>
							</Form.Item>

							<Form.Item
								name="proofOfDepositDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'proofOfDepositDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.proofOfDepositDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.proofOfDeposit)}`}
								/>
							</Form.Item>

							<Form.Item
								name={'surveyorDocument'}
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'surveyorDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.surveyorDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.surveyorReport)}`}
								/>
							</Form.Item>
							<Form.Item
								name={'reportOnTitleDocument'}
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'reportOnTitleDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.reportOnTitleDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.reportOnTitle)}`}
								/>
							</Form.Item>
							<Form.Item
								name={'existingMortgageStatementDocument'}
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'existingMortgageStatementDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.existingMortgageStatementDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.existingMortgage)}`}
								/>
							</Form.Item>
							<Form.Item
								name="existingTenancyDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'existingTenancyDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.existingTenancyDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.existingTenancy)}`}
								/>
							</Form.Item>
							<Form.Item
								name={'energyPerformanceDocument'}
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'energyPerformanceDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.energyPerformanceDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.energyPerformance)}`}
								/>
							</Form.Item>

							<Form.Item
								name="ews1FormDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'ews1FormDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.ews1FormDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.ews1Form)}`}
								/>
							</Form.Item>

							<Form.Item
								name="valuationDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'valuationDocument'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.valuationDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.validationReport)}`}
								/>
							</Form.Item>

							<Form.Item
								name="chineseIdImgFront"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'chineseIdImgFront'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.chineseIdImgFront || []}
									title={t(i18nKey.onboarding.request.supportingDocument.chineseIdImgFront)}
								/>
							</Form.Item>

							<Form.Item
								name="chineseIdImgBack"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'chineseIdImgBack'}
									onDownload={onFileDownload}
									disabled
									inputFiles={onboardingStore.supportingDocuments?.chineseIdImgBack || []}
									title={t(i18nKey.onboarding.request.supportingDocument.chineseIdImgBack)}
								/>
							</Form.Item>

							<Form.Item
								name="bankCardImg"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									typeId={'bankCardImg'}
									disabled
									onDownload={onFileDownload}
									inputFiles={onboardingStore.supportingDocuments?.bankCardImg || []}
									title={t(i18nKey.onboarding.request.supportingDocument.bankCardImg)}
								/>
							</Form.Item>

							<FileUploader
								typeId={'borrowerIndividualOthers'}
								onDownload={onFileDownload}
								disabled
								inputFiles={onboardingStore.supportingDocuments?.borrowerIndividualOthers || []}
								title={`${t(i18nKey.onboarding.request.supportingDocument.otherDocs)}`}
								accept={DEFAULT_ALLOWED_OTHER_FILES}
							/>
						</div>
					</Form>
				</Spin>
			</PinkBox>
		</>
	);
};

export default observer(SupportingDocuments);
